import { Box, styled, Typography } from "@mui/material";
import { useBackofficeConstant } from "components/context/ConstantProvider";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDealFinancialsHelpers } from "../pages/DealFinancials";

const MULTIPLIER_CODE = "MILLION"; // Default multiplier code
const UNIT_MULTIPLIER_CODE = "UNIT"; // Default multiplier code

const useFinancialsTableHelpers = (deal) => {
  const { multipliers } = useBackofficeConstant("financials");
  const currency = useBackofficeConstant("backoffice.Currency", deal?.currency);
  const { t } = useTranslation("translation");
  const { getColumnLabel, previousLastDayOfPeriod } =
    useDealFinancialsHelpers();

  const getLineLabel = (financial_data_type, multiplier_code) => {
    const multiplier = multipliers.find((m) => m.value === multiplier_code);

    let unit_str = financial_data_type.unit
      ? ` (${financial_data_type.unit})`
      : "";
    if (multiplier) {
      unit_str = unit_str.replace("{multiplier}", multiplier.symbol);
    }
    if (financial_data_type.with_currency) {
      unit_str = unit_str.replace("{currency}", currency.symbol);
    }
    return `${t(financial_data_type.name)}${unit_str}`;
  };

  const getTableTitle = (closing_month, multiplier_code) => {
    const multiplier = multipliers.find((m) => m.value === multiplier_code);
    const closing_date_str = dayjs()
      .month(closing_month - 1)
      .endOf("month")
      .format("DD/MM");
    return `${t("Year end")} - ${closing_date_str} - ${
      multiplier ? multiplier.symbol : ""
    }${currency.symbol}`;
  };

  const getColumns = ({ deal, valuation_period }) => {
    const last_financial_date = (
      deal.last_financial_date ? dayjs(deal.last_financial_date) : dayjs()
    ).endOf("month");

    // Anchor fiscal-year computation on the selected valuation period, like
    // the RingBack does (ring-backoffice/visualization/viz.py ReportingTable).
    // Falling back on last_financial_date keeps the legacy behaviour when the
    // valuation period is missing.
    const reference_date = (
      valuation_period?.date ? dayjs(valuation_period.date) : last_financial_date
    ).endOf("month");

    const last_fiscal_year_date = previousLastDayOfPeriod(
      reference_date,
      deal.closing_month,
      "YEAR",
    );
    const next_fiscal_year_date = last_fiscal_year_date.add(1, "year");

    const columns = [];

    const pushColumnAndComparaison = (column) => {
      columns.push({
        valueKey: column,
        type: "value",
        display: getColumnLabel({
          ...column,
          closing_month: deal.closing_month,
        }),
      });
      columns.push({
        valueKey: column,
        type: "growth",
        display: { label: "vs N-1" },
        otherValueKey: {
          ...column,
          categories: ["ACTUAL", "FORECAST"],
          date: column.date.subtract(1, "year"),
        },
      });
    };

    pushColumnAndComparaison({
      category: "ACTUAL",
      date: last_fiscal_year_date.add(-3, "year"),
      period: "YEAR",
    });
    pushColumnAndComparaison({
      category: "ACTUAL",
      date: last_fiscal_year_date.add(-2, "year"),
      period: "YEAR",
    });
    pushColumnAndComparaison({
      category: "ACTUAL",
      date: last_fiscal_year_date.add(-1, "year"),
      period: "YEAR",
    });
    pushColumnAndComparaison({
      category: "ACTUAL",
      date: last_fiscal_year_date,
      period: "YEAR",
    });

    // adding the current fiscal year column
    if (next_fiscal_year_date.isSame(last_financial_date)) {
      pushColumnAndComparaison({
        category: "ACTUAL",
        date: last_financial_date,
        period: "YEAR",
      });
    } else {
      if (deal.last_column_type === "FORCAST") {
        pushColumnAndComparaison({
          category: "BUSINESS_PLAN",
          date: next_fiscal_year_date,
          period: "YEAR",
        });
      } else if (deal.last_column_type === "YEAR_TO_DATE") {
        pushColumnAndComparaison({
          category: "ACTUAL",
          date: last_financial_date,
          period:
            last_financial_date.month() === deal.closing_month - 1
              ? "YEAR"
              : "YEAR_TO_DATE",
        });
      }
    }

    // adding the budget column - mirrors the RingBack logic
    // (ring-backoffice/visualization/viz.py ReportingTable._columns_kwargs)
    // months = number of months elapsed in the fiscal year containing reference_date
    const month_in_fy =
      (reference_date.month() + 1 - deal.closing_month - 1 + 12) % 12;

    if (
      deal.display_budget == "IN_FIRST_MONTHS" &&
      (month_in_fy === 10 || month_in_fy === 11)
    ) {
      // last 2 months of the fiscal year: show next FY budget
      pushColumnAndComparaison({
        category: "BUDGET",
        date: next_fiscal_year_date.add(1, "Year"),
        period: "YEAR",
      });
    } else if (
      deal.display_budget == "IN_FIRST_MONTHS" &&
      month_in_fy <= 7
    ) {
      // first 8 months of the fiscal year: show current FY budget
      pushColumnAndComparaison({
        category: "BUDGET",
        date: next_fiscal_year_date,
        period: "YEAR",
      });
    }

    return columns.slice(-8); // Return the last 4 columns
  };

  const getLines = ({ financial_data_types, getLineLabel }) => {
    const lines = [];
    const revenueType = financial_data_types.find(
      (type) => type.gs_code_name === "revenue",
    );
    financial_data_types
      .sort((a, b) => a.order - b.order)
      .filter((type) => type.display_in_reporting)
      .forEach((type) => {
        lines.push({
          type: "value",
          valueKey: {
            valueType: type,
          },
          display: {
            label: getLineLabel(type, MULTIPLIER_CODE),
          },
          value_type: type,
        });
        if (["grossmargin", "ebitda"].includes(type.gs_code_name)) {
          lines.push({
            type: "partial",
            valueKey: {
              valueType: type,
            },
            otherValueKey: {
              valueType: revenueType,
            },
            display: { label: "% revenue" },
          });
        }
      });
    return lines;
  };

  const getGrowthCellString = (val1, val2) => {
    const val1IsNull = val1 === null || val1 === undefined;
    const val2IsNull = val2 === null || val2 === undefined;
    if (val1IsNull && val2IsNull) return "-";
    if (val1IsNull || val2IsNull) {
      return "n.a.";
    }
    if (Math.min(val1, val2) < 0) {
      return "n.r.";
    }
    let plus = "";
    if (val2 - val1 > 0) {
      plus = "+";
    }
    if (val1 === 0) {
      return "n.r.";
    }
    const result = (val2 - val1) / val1;
    if (result > 9.999) {
      return "n.r.";
    }
    return `${plus}${(result * 100).toFixed(1)}%`;
  };

  const getPartialCellString = (val1, val2, cache) => {
    const val1IsNull = val1 === null || val1 === undefined;
    const val2IsNull = val2 === null || val2 === undefined;
    if (val1IsNull && val2IsNull) return "-";
    if (val1IsNull || val2IsNull) {
      return "n.a.";
    }
    if (val1 === 0) {
      return "n.r.";
    }
    const result = val2 / val1;
    return `${(result * 100).toFixed(1)}%`;
  };

  const getFinancialTableSubtitle = (last_financial_date) => {
    if (!last_financial_date) {
      return "No financial data available";
    }
    const date = dayjs(last_financial_date);
    const month = date.format("MMM");
    const year = date.format("YY");
    return t("End") + ` ${month}-${year}`;
  };

  return {
    getLineLabel,
    getTableTitle,
    getColumns,
    getLines,
    getFinancialTableSubtitle,
    getGrowthCellString,
    getPartialCellString,
  };
};

const useFinancialDataValsCache = (dealId, financial_data_vals) => {
  const { multipliers } = useBackofficeConstant("financials");
  const dico = useMemo(() => {
    return Object.fromEntries(
      financial_data_vals.map((item) => [item.id, item]),
    );
  }, [financial_data_vals]);

  const getValue = (valueKey, multiplier_code) => {
    const { valueType, date, period, category } = valueKey;
    const year = date.year();
    const month = date.month() + 1; // month is 0-index
    const valueTypeId = valueType.id;
    const dest_multiplier = multipliers.find(
      (m) => m.value === multiplier_code,
    );
    const source_multiplier = multipliers.find(
      (m) => m.value === valueType.multiplier,
    );
    const id = `${dealId}-${year}-${month}-${category}-${period}-${valueTypeId}`;
    const financial_data_val = dico[id];

    return {
      financial_data_val: financial_data_val,
      value:
        financial_data_val &&
        financial_data_val.value &&
        (financial_data_val.value * source_multiplier.coefficient) /
          dest_multiplier.coefficient,
    };
  };

  return { getValue };
};

const StyledTable = styled("table")({
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "9.6px",
  textAlign: "center",
  "& th, & td": {
    lineHeight: "1.1rem",
    padding: "0 6px",
    textAlign: "center",
  },
  "& .col-def-header": {
    width: "12em",
  },
  "& .col-def-value": {
    width: "6em",
  },
  "& .col-def-growth": {
    width: "5em",
  },
  "& tr.row-header th": {
    backgroundColor: "#94dc5c",
  },
  "& tr.row-header th.col-header": {
    textAlign: "left",
    fontWeight: "normal",
  },
  "& tr.row-header th.col-value": {
    fontWeight: "bold",
    lineHeight: "0.8rem",
  },
  "& tr.row-header th.col-value .subtitle": {},
  "& tr.row-header th.col-growth": {
    fontWeight: "normal",
    fontStyle: "italic",
  },
  "& td.col-header": {
    textAlign: "left",
  },
  "& td.col-value": {},
  "& td.col-growth": {
    backgroundColor: "#e3f8cf",
  },
  "& .row-partial": {
    fontStyle: "italic",
  },
  "& tbody": {
    borderBottom: "solid 1px lightgrey",
  },
  "& caption": {
    paddingTop: "0.15rem",
    captionSide: "bottom",
    textAlign: "right",
    fontSize: "90%",
    fontWeight: 300,
    fontStyle: "italic",
  },
});

const FinancialCell = ({ col, cache, line }) => {
  const { getGrowthCellString, getPartialCellString } =
    useFinancialsTableHelpers();
  const valueKey = { ...col.valueKey, ...line.valueKey };
  const content = (function () {
    if (col.type === "growth" && line.type === "value") {
      const otherValueKeys = col.otherValueKey.categories.map((c) => ({
        ...col.valueKey,
        ...line.valueKey,
        ...col.otherValueKey,
        ...line.otherValueKey,
        category: c,
      }));
      const { value: value1, financial_data_val: financial_data_val1 } =
        cache.getValue(valueKey, MULTIPLIER_CODE);
      const values2 = otherValueKeys.map((k) => {
        const { value: value2 } = cache.getValue(k, MULTIPLIER_CODE);
        return value2;
      });
      const value2 = values2.find((v) => v !== undefined && v != null);
      return getGrowthCellString(value2, value1);
    }
    if (col.type === "value" && line.type === "partial") {
      const otherValueKey = {
        ...col.valueKey,
        ...line.valueKey,
        ...col.otherValueKey,
        ...line.otherValueKey,
      };
      const { value: value1, financial_data_val: financial_data_val1 } =
        cache.getValue(valueKey, MULTIPLIER_CODE);
      const { value: value2, financial_data_val: financial_data_val2 } =
        cache.getValue(otherValueKey, MULTIPLIER_CODE);
      return getPartialCellString(value2, value1);
    }
    if (col.type === "growth" && line.type === "partial") {
      return "";
    }
    if (valueKey.valueType.gs_code_name === "employees") {
      const { value, financial_data_val } = cache.getValue(
        valueKey,
        UNIT_MULTIPLIER_CODE,
      );
      return value?.toFixed(0) || "-";
    } else {
      const { value, financial_data_val } = cache.getValue(
        valueKey,
        MULTIPLIER_CODE,
      );
      if (Number.isFinite(value)) return value?.toFixed(2) || "-";
      return "n.a.";
    }
  })();

  return <td className={`col-${col.type}`}>{content}</td>;
};

const FinancialsTable = ({
  deal,
  fund,
  valuation_period,
  financial_data_types,
  financial_data_vals,
}) => {
  const {
    getLineLabel,
    getTableTitle,
    getColumns,
    getLines,
    getFinancialTableSubtitle,
  } = useFinancialsTableHelpers(deal);
  const cache = useFinancialDataValsCache(deal.id, financial_data_vals);
  const columns = getColumns({ deal, valuation_period });
  const lines = getLines({ financial_data_types, getLineLabel });

  if (!financial_data_vals) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100px"
      >
        <Typography variant="body1">No financials data available.</Typography>
      </Box>
    );
  }
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100px"
    >
      <StyledTable>
        <thead>
          <tr className={"row-header"}>
            <th className="col-header">
              {getTableTitle(deal.closing_month, MULTIPLIER_CODE)}
            </th>
            {columns.map((col, index) => (
              <th key={index} className={`col-${col.type}`}>
                {col.display.label}
                <br />
                <span className="subtitle">{col.display.subtitle}</span>
              </th>
            ))}
          </tr>
        </thead>
        <colgroup>
          <col className="col-def-header" />
          {columns.map((col, index) => (
            <col key={index} className={`col-def-${col.type}`} />
          ))}
        </colgroup>
        <tbody>
          {lines.map((line, index) => (
            <tr key={index} className={`row-${line.type}`}>
              <td className={`col-header`}>{line.display.label}</td>
              {columns.map((col, colIndex) => (
                <FinancialCell
                  key={colIndex}
                  col={col}
                  cache={cache}
                  line={line}
                />
              ))}
            </tr>
          ))}
        </tbody>
        <caption>{getFinancialTableSubtitle(deal.last_financial_date)}</caption>
        <caption>&nbsp;</caption>
      </StyledTable>
    </Box>
  );
};

export default FinancialsTable;
